// frontend/src/pages/Forecast.jsx

import React, { useState, useMemo, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import Tile from '../components/layout/Tile';
import ContactSupportButton from '../components/common/ContactSupportButton';
import SaveDiscardButtons from '../components/common/SaveDiscardButtons';
import BarEnvelopeChart from '../components/charts/BarEnvelopeChart';
import ForecastGrid from '../components/forecastgrid/ForecastGrid';
import { useDemandTimeline, useForecastKpi } from '../hooks/useForecastData';
import { useSalesKpi } from '../hooks/useSalesData';
import { useFormatters } from '../hooks/useFormatters';
import { useGridChartScope, DrillDownChip } from '../features/item-filter';
import { useInAppSyncStatus } from '../hooks/useInAppSyncStatus';
import { FORECAST_RUN, forecastRunState, forecastsMissing } from '../utils/syncGate';
import '../styles/pages.css';

// Why there is nothing to override yet, read off the forecast run the import
// started; with no run to wait on (none enqueued yet) it is "not ready yet".
function pendingKey(forecastRun) {
  if (forecastRun === FORECAST_RUN.CALCULATING) return 'forecast.pending.calculating';
  if (forecastRun === FORECAST_RUN.FAILED) return 'forecast.pending.failed';
  return 'forecast.pending.notReady';
}

const ForecastPending = ({ forecastRun }) => {
  const { t } = useTranslation();
  return (
    <div className="page-container">
      <div className="tiles-row">
        <Tile width="full" title={t('nav.forecast')}>
          <Typography color="textSecondary" role="status">
            {t(pendingKey(forecastRun))}
          </Typography>
          {forecastRun === FORECAST_RUN.FAILED && <ContactSupportButton />}
        </Tile>
      </div>
    </div>
  );
};

/**
 * The Forecast page, once the store has a forecast. Before that the sidebar,
 * the phone's More sheet and the command palette do not offer it
 * (`utils/syncGate.forecastsMissing`), and a tab or link that still opens the
 * route says why instead; the page replaces that message by itself when the
 * polled sync-status read says the forecast is in.
 */
const Forecast = () => {
  const syncStatus = useInAppSyncStatus();
  if (forecastsMissing(syncStatus)) {
    return <ForecastPending forecastRun={forecastRunState(syncStatus)} />;
  }
  return <ForecastWorkspace />;
};

const ForecastWorkspace = () => {
  const { t } = useTranslation();
  const { formatCompact } = useFormatters();
  const { data: salesKpiData, error: salesKpiError } = useSalesKpi();
  const { data: forecastKpiData, error: forecastKpiError } = useForecastKpi();

  // Page scope: filters drive both the grid and the chart; drill-down
  // replaces the chart's filter scope when a single row is selected.
  // The unsaved-edits guard lives in ForecastGrid (it owns the editable
  // cells); the scope hook reaches it through the grid's imperative ref.
  const forecastGridRef = useRef(null);
  const scope = useGridChartScope({
    beforeChange: () => forecastGridRef.current?.confirmDiscardEdits() ?? true,
  });
  const {
    filter: itemFilter,
    selectedLocation, setSelectedLocation,
    selectedProductCategory, setSelectedProductCategory,
    promotedOnly, setPromotedOnly,
    drillDown, drillDownRowId, handleRowSelect, clearDrillDown,
    chartExtraParams,
  } = scope;

  const [forecastEditing, setForecastEditing] = useState({ count: 0, saving: false });
  // Slot element the grid portals its Import/Export buttons into — the grid
  // owns their state (filters, unsaved-edits guard) but they render
  // in this page's title row, matching the app-wide placement standard.
  const [gridHeaderActionsEl, setGridHeaderActionsEl] = useState(null);

  const timelineCollapsedItems = useMemo(() => {
    const currency = salesKpiData?.currency || forecastKpiData?.currency;
    const items = [];
    if (salesKpiError) items.push({ label: t('home.metrics.sales'), value: salesKpiError });
    else if (salesKpiData) items.push({ label: t('home.metrics.sales'), value: formatCompact(salesKpiData.mtd_value, currency) });
    if (forecastKpiError) items.push({ label: t('forecast.demandTimeline.forecast'), value: forecastKpiError });
    else if (forecastKpiData) items.push({ label: t('forecast.demandTimeline.forecast'), value: formatCompact(forecastKpiData.total_value, currency) });
    return items.length ? items : null;
  }, [salesKpiData, salesKpiError, forecastKpiData, forecastKpiError, formatCompact, t]);

  return (
    <div className="page-container">
      {/* Demand Timeline: sales history → forecast demand */}
      <div className="tiles-row">
        <Tile
          width="full"
          title={t('forecast.demandTimelineTitle')}
          titleState={<DrillDownChip drillDown={drillDown} onClear={clearDrillDown} />}
          subtitle={t('forecast.demandTimelineSubtitle')}
          subtitleAutoHide
          collapsible storageKey="demand-timeline" collapsedItems={timelineCollapsedItems}>
          <BarEnvelopeChart
            useData={useDemandTimeline}
            i18nPrefix="forecast.demandTimeline"
            chartId="demand_timeline"
            controls={{ metric: true }}
            extraParams={chartExtraParams}
            navigator
            navigatorWindow={{ before: 6, after: 6 }}
          />
        </Tile>
      </div>

      {/* Forecast Grid */}
      <div className="tiles-row">
        <Tile
          width="full"
          title={t('nav.forecast')}
          subtitle={t('forecast.subtitle')}
          subtitleAutoHide
          editActions={
            <SaveDiscardButtons
              count={forecastEditing.count}
              saving={forecastEditing.saving}
              onSave={() => forecastGridRef.current?.save()}
              onDiscard={() => forecastGridRef.current?.discard()}
            />
          }
          headerActions={<Box ref={setGridHeaderActionsEl} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }} />}
        >
          <ForecastGrid
            ref={forecastGridRef}
            headerActionsEl={gridHeaderActionsEl}
            filter={itemFilter}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            selectedProductCategory={selectedProductCategory}
            setSelectedProductCategory={setSelectedProductCategory}
            promotedOnly={promotedOnly}
            setPromotedOnly={setPromotedOnly}
            onRowSelect={handleRowSelect}
            selectedRowId={drillDownRowId}
            onEditingChange={setForecastEditing}
          />
        </Tile>
      </div>
    </div>
  );
};

export default Forecast;
